import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { login, getProfileRole, isServiceProviderApproved } from '../services/auth/auth.service';
import { supabase } from '../services/supabase/client';
import { getAuthErrorMessage } from '../features/auth';
import { useTranslation } from 'react-i18next';
import styles from './Login.module.css';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    
    try {
      const result = await login({ email, password });

      if (result.success) {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();

        if (user) {
          // Check if user account is active
          // maybeSingle: a missing profile row means "not disabled", not a 406.
          const { data: profile } = await supabase
            .from('profiles')
            .select('is_active')
            .eq('id', user.id)
            .maybeSingle();

          if (profile && profile.is_active === false) {
            // Account is disabled - logout and show error
            await supabase.auth.signOut();
            setError(t('pages.login.accountDisabled'));
            return;
          }
        }

        // Redirect based on stored profile role
        const role = await getProfileRole();
        if (role === 'admin') {
          // Redirect admin users to admin dashboard
          navigate('/admin');
        } else if (role === 'technician') {
          const approved = await isServiceProviderApproved();
          if (!approved) {
            // Redirect to approval pending page
            navigate('/service-provider/approval-pending');
          } else {
            navigate('/service-provider/dashboard');
          }
        } else {
          navigate('/dashboard');
        }
      } else {
        // Login failed - show a friendly, translated message
        const { key, params } = getAuthErrorMessage(result.error, 'login');
        setError(t(key, params));
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(t('pages.login.unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      {/* Top Bar with Logo */}
      <div className={styles.topBar}>
        <Link to="/" className={styles.logo}>
        <svg width="160" height="12" viewBox="0 0 160 12" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M3.45073 9.51603C5.35651 9.51603 6.90146 7.97109 6.90146 6.06531C6.90146 4.15953 5.35651 2.61459 3.45073 2.61459C1.54494 2.61459 0 4.15953 0 6.06531C0 7.97109 1.54494 9.51603 3.45073 9.51603Z" fill="black"/>
<path d="M13.3983 11.9429V11.0263L22.4295 1.64461H13.9375V0.0270844H25.6107V0.916719L16.5256 10.3523H25.7185V11.9429H13.3714H13.3983Z" fill="#020202"/>
<path d="M33.9144 11.9429V0.0270844H45.6414V1.64461H35.7745V5.06838H44.644V6.65895H35.7745V10.3523H45.7762V11.9429H33.9144Z" fill="#020202"/>
<path d="M54.2677 11.9427V0.0269646H56.1009L63.5685 7.60238C63.5685 7.60238 63.8381 7.87197 64.0268 8.06069C64.1886 8.2494 64.3773 8.43811 64.566 8.62682C64.7547 8.81553 64.8895 8.97728 65.0243 9.13903H65.1591C65.1591 8.92336 65.1591 8.65378 65.1591 8.30331C65.1591 7.95285 65.1591 7.68326 65.1591 7.41367V0H66.9923V11.9158H65.2669L57.7454 4.25949C57.4758 3.98991 57.2063 3.69336 56.9097 3.39681C56.6401 3.10027 56.3975 2.83068 56.2357 2.66893H56.1009C56.1009 2.85764 56.1009 3.15418 56.1009 3.50464C56.1009 3.88206 56.1009 4.23253 56.1009 4.60995V11.9158H54.2677V11.9427Z" fill="#020202"/>
<path d="M79.9327 11.9429V1.69853H74.5409V0.0270844H87.2116V1.69853H81.8198V11.9429H79.9327Z" fill="#020202"/>
<path d="M94.8415 11.9429V0.0270844H103.792C104.574 0.0270844 105.248 0.188828 105.76 0.512333C106.299 0.835839 106.703 1.26719 106.946 1.80636C107.189 2.34554 107.35 2.96559 107.35 3.63956C107.35 4.36745 107.162 5.04142 106.784 5.66147C106.407 6.25457 105.841 6.71287 105.086 7.00941L107.674 11.9968H105.544L103.172 7.33292H96.6747V11.9968H94.7875L94.8415 11.9429ZM96.7286 5.68843H103.36C104.034 5.68843 104.547 5.49972 104.924 5.1223C105.301 4.74487 105.49 4.23266 105.49 3.63956C105.49 3.23518 105.409 2.88471 105.248 2.58816C105.086 2.29162 104.843 2.04899 104.52 1.88724C104.196 1.72548 103.819 1.64461 103.387 1.64461H96.7555V5.71538L96.7286 5.68843Z" fill="#020202"/>
<path d="M115.761 11.9429V0.0270844H117.648V11.9429H115.761Z" fill="#020202"/>
<path d="M125.305 11.9429L131.37 0.0270844H133.554L139.62 11.9429H137.544L136.115 9.13915H128.702L127.273 11.9429H125.305ZM129.456 7.57555H135.333L133.581 4.04394C133.5 3.90915 133.419 3.72043 133.312 3.50476C133.204 3.28909 133.096 3.04647 132.988 2.80384C132.88 2.56121 132.772 2.31858 132.664 2.12987C132.557 1.94116 132.503 1.7794 132.449 1.69853H132.314C132.206 1.94116 132.071 2.21074 131.937 2.53424C131.802 2.85775 131.667 3.1543 131.532 3.42389C131.397 3.69348 131.29 3.90915 131.209 4.0709L129.429 7.6025L129.456 7.57555Z" fill="#020202"/>
<path d="M147.276 11.9427V0.0269646H149.109L156.576 7.60238C156.576 7.60238 156.846 7.87197 157.035 8.06069C157.196 8.2494 157.385 8.43811 157.574 8.62682C157.763 8.81553 157.897 8.97728 158.032 9.13903H158.167C158.167 8.92336 158.167 8.65378 158.167 8.30331C158.167 7.95285 158.167 7.68326 158.167 7.41367V0H160V11.9158H158.275L150.753 4.25949C150.484 3.98991 150.214 3.69336 149.918 3.39681C149.648 3.10027 149.405 2.83068 149.244 2.66893H149.109C149.109 2.85764 149.109 3.15418 149.109 3.50464C149.109 3.88206 149.109 4.23253 149.109 4.60995V11.9158H147.276V11.9427Z" fill="#020202"/>
</svg>

        </Link>
      </div>

      {/* Main Content Section */}
      <div className={styles.contentSection}>
        {/* Logo for Tablet/Desktop only */}
        <Link to="/" className={styles.logoLarge}>
          <svg width="200" height="15" viewBox="0 0 160 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.45073 9.51603C5.35651 9.51603 6.90146 7.97109 6.90146 6.06531C6.90146 4.15953 5.35651 2.61459 3.45073 2.61459C1.54494 2.61459 0 4.15953 0 6.06531C0 7.97109 1.54494 9.51603 3.45073 9.51603Z" fill="black"/>
            <path d="M13.3983 11.9429V11.0263L22.4295 1.64461H13.9375V0.0270844H25.6107V0.916719L16.5256 10.3523H25.7185V11.9429H13.3714H13.3983Z" fill="#020202"/>
            <path d="M33.9144 11.9429V0.0270844H45.6414V1.64461H35.7745V5.06838H44.644V6.65895H35.7745V10.3523H45.7762V11.9429H33.9144Z" fill="#020202"/>
            <path d="M54.2677 11.9427V0.0269646H56.1009L63.5685 7.60238C63.5685 7.60238 63.8381 7.87197 64.0268 8.06069C64.1886 8.2494 64.3773 8.43811 64.566 8.62682C64.7547 8.81553 64.8895 8.97728 65.0243 9.13903H65.1591C65.1591 8.92336 65.1591 8.65378 65.1591 8.30331C65.1591 7.95285 65.1591 7.68326 65.1591 7.41367V0H66.9923V11.9158H65.2669L57.7454 4.25949C57.4758 3.98991 57.2063 3.69336 56.9097 3.39681C56.6401 3.10027 56.3975 2.83068 56.2357 2.66893H56.1009C56.1009 2.85764 56.1009 3.15418 56.1009 3.50464C56.1009 3.88206 56.1009 4.23253 56.1009 4.60995V11.9158H54.2677V11.9427Z" fill="#020202"/>
            <path d="M79.9327 11.9429V1.69853H74.5409V0.0270844H87.2116V1.69853H81.8198V11.9429H79.9327Z" fill="#020202"/>
            <path d="M94.8415 11.9429V0.0270844H103.792C104.574 0.0270844 105.248 0.188828 105.76 0.512333C106.299 0.835839 106.703 1.26719 106.946 1.80636C107.189 2.34554 107.35 2.96559 107.35 3.63956C107.35 4.36745 107.162 5.04142 106.784 5.66147C106.407 6.25457 105.841 6.71287 105.086 7.00941L107.674 11.9968H105.544L103.172 7.33292H96.6747V11.9968H94.7875L94.8415 11.9429ZM96.7286 5.68843H103.36C104.034 5.68843 104.547 5.49972 104.924 5.1223C105.301 4.74487 105.49 4.23266 105.49 3.63956C105.49 3.23518 105.409 2.88471 105.248 2.58816C105.086 2.29162 104.843 2.04899 104.52 1.88724C104.196 1.72548 103.819 1.64461 103.387 1.64461H96.7555V5.71538L96.7286 5.68843Z" fill="#020202"/>
            <path d="M115.761 11.9429V0.0270844H117.648V11.9429H115.761Z" fill="#020202"/>
            <path d="M125.305 11.9429L131.37 0.0270844H133.554L139.62 11.9429H137.544L136.115 9.13915H128.702L127.273 11.9429H125.305ZM129.456 7.57555H135.333L133.581 4.04394C133.5 3.90915 133.419 3.72043 133.312 3.50476C133.204 3.28909 133.096 3.04647 132.988 2.80384C132.88 2.56121 132.772 2.31858 132.664 2.12987C132.557 1.94116 132.503 1.7794 132.449 1.69853H132.314C132.206 1.94116 132.071 2.21074 131.937 2.53424C131.802 2.85775 131.667 3.1543 131.532 3.42389C131.397 3.69348 131.29 3.90915 131.209 4.0709L129.429 7.6025L129.456 7.57555Z" fill="#020202"/>
            <path d="M147.276 11.9427V0.0269646H149.109L156.576 7.60238C156.576 7.60238 156.846 7.87197 157.035 8.06069C157.196 8.2494 157.385 8.43811 157.574 8.62682C157.763 8.81553 157.897 8.97728 158.032 9.13903H158.167C158.167 8.92336 158.167 8.65378 158.167 8.30331C158.167 7.95285 158.167 7.68326 158.167 7.41367V0H160V11.9158H158.275L150.753 4.25949C150.484 3.98991 150.214 3.69336 149.918 3.39681C149.648 3.10027 149.405 2.83068 149.244 2.66893H149.109C149.109 2.85764 149.109 3.15418 149.109 3.50464C149.109 3.88206 149.109 4.23253 149.109 4.60995V11.9158H147.276V11.9427Z" fill="#020202"/>
          </svg>
        </Link>
<div>

</div>
        <form id="loginForm" onSubmit={handleSubmit} className={styles.formContainer}>
          {/* Header Section */}
          <div className={styles.headerSection}>
            <h1 className={styles.heading}>{t('pages.login.welcomeBack')}</h1>
            <p className={styles.subheading}>{t('pages.login.subtitle')}</p>
          </div>

          {/* Email Input */}
          <input
            id="email"
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('pages.login.emailPlaceholder')}
            className={styles.inputField}
            required
          />

          {/* Password Input */}
          <input
            id="password"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('pages.login.passwordPlaceholder')}
            className={styles.inputField}
            required
          />

          {/* Forgot Password Link */}
          <Link to="/forgot-password" className={styles.forgotPassword}>
            {t('pages.login.forgotPassword')}
          </Link>

          {/* Error Message */}
          {error && (
            <div className={styles.errorMessage}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
<div className={styles.buttonSectionForm1}>


          {/* Button Section */}
          <div className={styles.buttonSection}>
            <button
              type="submit"
              disabled={isLoading}
              className={styles.signInButton}
            >
              {isLoading ? t('pages.login.signingIn') : t('pages.login.signIn')}
            </button>
          </div>

          {/* Sign Up Link */}
          <div className={styles.signUpSection}>
            <p>
              {t('pages.login.noAccount')}{' '}
              <Link to="/register" className={styles.signUpLink}>
                {t('pages.login.signUp')}
              </Link>
            </p>
          </div>
          </div>
        </form>
        
      </div>
      <div className={styles.buttonSectionForm2}>
          {/* Button Section */}
          <div className={styles.buttonSection}>
            <button
              type="submit"
              form="loginForm"
              disabled={isLoading}
              className={styles.signInButton}
            >
              {isLoading ? t('pages.login.signingIn') : t('pages.login.signIn')}
            </button>
          </div>

          {/* Sign Up Link */}
          <div className={styles.signUpSection}>
            <p>
              {t('pages.login.noAccount')}{' '}
              <Link to="/register" className={styles.signUpLink}>
                {t('pages.login.signUp')}
              </Link>
            </p>
          </div>
          </div>
    </div>
  );
};

export default Login;
